import React, { useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../ui/Dialog";
import { Input } from "../../ui/Input";
import { Search, X, ArrowUp, ArrowDown, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { cn } from "../../../lib/utils";
import { SearchResult } from "../../../utils/docs/searchIndex";
import { useDocsSearch } from "../../../hooks/docs/useDocsSearch";
import { ContentPage } from "../../../utils/docs/contentLoader";

interface DocsSearchBarProps {
  pages: ContentPage[];
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

export function DocsSearchBar({
  pages,
  isOpen,
  onOpenChange,
}: DocsSearchBarProps) {
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { results, isIndexing, search } = useDocsSearch(pages);

  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
      setQuery("");
      setSelectedIndex(0);
    }
  }, [isOpen]);

  useEffect(() => {
    if (query.trim()) {
      search(query);
    }
  }, [query, search]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((prev) => (prev < results.length - 1 ? prev + 1 : prev));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((prev) => (prev > 0 ? prev - 1 : 0));
    } else if (e.key === "Enter" && results[selectedIndex]) {
      e.preventDefault();
      handleSelectResult(results[selectedIndex]);
    } else if (e.key === "Escape") {
      onOpenChange(false);
    }
  };

  const handleSelectResult = (result: SearchResult) => {
    navigate(`/docs/${result.page.path}`);
    onOpenChange(false);
    setQuery("");
    setSelectedIndex(0);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl p-0 bg-[var(--su-bg-primary)] border-[var(--su-border-primary)] rounded-none">
        <DialogHeader className="p-4 border-b border-[var(--su-border-primary)]">
          <DialogTitle className="text-[var(--su-text-primary)]">
            Search Documentation
          </DialogTitle>
        </DialogHeader>

        <div className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[var(--su-text-muted)]" />
            <Input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search docs..."
              className="pl-10 bg-[var(--su-bg-secondary)] border-[var(--su-border-primary)] text-[var(--su-text-primary)] placeholder:text-[var(--su-text-muted)] rounded-none"
            />
            {isIndexing && (
              <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[var(--su-text-muted)] animate-spin" />
            )}
          </div>

          {query.trim() && (
            <div className="mt-4 max-h-96 overflow-y-auto">
              {results.length === 0 ? (
                <div className="p-8 text-center text-[var(--su-text-muted)]">
                  No results found for &ldquo;{query}&rdquo;
                </div>
              ) : (
                <div className="space-y-1">
                  {results.map((result, index) => (
                    <button
                      key={result.page.path}
                      onClick={() => handleSelectResult(result)}
                      className={cn(
                        "w-full text-left p-3 rounded-none border transition-colors",
                        index === selectedIndex
                          ? "bg-[var(--su-purple-900)] border-[var(--su-purple-accent)]"
                          : "bg-[var(--su-bg-secondary)] border-[var(--su-border-primary)] hover:bg-[var(--su-bg-tertiary)]",
                      )}
                    >
                      <div className="font-medium text-[var(--su-text-primary)] mb-1">
                        {result.title}
                      </div>
                      {result.excerpt && (
                        <div className="text-sm text-[var(--su-text-muted)] line-clamp-2">
                          {result.excerpt}
                        </div>
                      )}
                      <div className="text-xs text-[var(--su-text-muted)] mt-1">
                        {result.page.path}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {!query.trim() && (
            <div className="mt-4 p-8 text-center text-[var(--su-text-muted)] text-sm">
              <div className="flex items-center justify-center gap-2 mb-2">
                <kbd className="px-2 py-1 bg-[var(--su-bg-secondary)] border border-[var(--su-border-primary)] rounded-none text-xs">
                  ⌘
                </kbd>
                <span>+</span>
                <kbd className="px-2 py-1 bg-[var(--su-bg-secondary)] border border-[var(--su-border-primary)] rounded-none text-xs">
                  K
                </kbd>
              </div>
              <p>Search documentation</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
